/**
 * A tela de início — igual à do painel antigo, e agora com efeito.
 *
 * O hero se dissolve com desfoque forte enquanto a câmera afunda, a tela fecha
 * no preto, e só então a rota muda. Nunca existe um quadro com as duas telas
 * visíveis: o véu preto acompanha a mesma variável do shader.
 *
 * ── O que Buscar faz ────────────────────────────────────────────────────
 *
 * Buscar SEMPRE vai às fontes. O que muda é o portão de coleta:
 *
 *   campo vazio  usa o vocabulário da tabela — os termos que a tela de
 *                Vocabulário gerencia, o comportamento normal do sistema.
 *
 *   com texto    usa aquela palavra NO LUGAR do vocabulário, só nesta
 *                execução. É sondagem, não edição do portão: nada é gravado
 *                na tabela `termo`.
 *
 * Os rádios escolhem em qual fonte. Vazio quer dizer as duas.
 *
 * Vale saber o custo: isto é rede, não banco. Uma varredura completa do PNCP
 * leva perto de uma hora e consome a conta do Licit Mais — para procurar no
 * que JÁ foi coletado, que responde na hora, o campo de busca fica em
 * Oportunidades.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, type Filtros } from "../api/cliente";
import { useAgua } from "../componentes/Agua";
import {
  FiltrosDaFonte,
  type ValoresFiltro,
} from "../componentes/FiltrosDaFonte";
import { useSessao } from "../sessao";
import css from "./Home.module.css";

/** A saudação do painel original, pela hora do dia. */
function saudacao() {
  const h = new Date().getHours();
  return h < 12 ? "Bom dia!" : h < 18 ? "Boa tarde!" : "Boa noite!";
}

/** `pncp` -> `PNCP`, `licitmais` -> `Licitmais`. */
const NOMES: Record<string, string> = { pncp: "PNCP", dadosapi: "DadosAPI" };

function rotulo(fonte: string) {
  return NOMES[fonte] ?? fonte.charAt(0).toUpperCase() + fonte.slice(1);
}

export function Home() {
  const [busca, setBusca] = useState("");
  // String vazia = todas as fontes. É o mesmo contrato da rota, que traduz
  // vazio em `termo="todas as fontes"` e roda `list(FONTES)` no runner — não
  // há um valor especial inventado aqui para significar "ambas".
  const [fonte, setFonte] = useState("");
  // Por fonte, e não um mapa só: trocar de fonte e voltar não pode perder o
  // que já tinha sido preenchido, e os campos de uma não valem para a outra.
  const [filtros, setFiltros] = useState<Record<string, ValoresFiltro>>({});
  const [mergulhando, setMergulhando] = useState(false);
  // AS FONTES RECOLHIDAS NO CELULAR (pedido de 2026-10-02): a busca de todo
  // dia é em todas, e cinco pílulas logo abaixo do campo pesavam mais que
  // ele. Uma linha diz qual está valendo e abre as opções. No desktop o
  // grupo continua à vista — o CSS só esconde até 560px.
  const [erro, setErro] = useState<string | null>(null);
  const { podeOperar } = useSessao();
  const { mergulhar, repousar } = useAgua();
  const navegar = useNavigate();

  useEffect(() => {
    repousar();
  }, [repousar]);

  // Se já havia varredura em andamento quando a página abriu, oferece entrar
  // nela — a conta é compartilhada, então isso é informação de todos.
  const atual = useQuery({ queryKey: ["rodada-atual"], queryFn: api.rodadaAtual });

  // As fontes vêm de `runner.FONTES` pela API. Uma cópia aqui quebraria a
  // promessa do módulo de coleta: acrescentar fonte é criar um arquivo e
  // registrá-lo no dicionário, sem tocar no frontend.
  const fontes = useQuery({ queryKey: ["fontes"], queryFn: api.fontes });

  // O número de termos vem do banco. Fixar "18" na tela é o mesmo erro da
  // lista de fontes: o vocabulário é editável em /vocabulario, e um rótulo
  // desatualizado mente exatamente para quem foi conferir.
  const ativos = useQuery({
    queryKey: ["termos", "ativo"],
    queryFn: () => api.termos("ativo"),
  });

  // A BUSCA IMEDIATA NO ACERVO (pedido da gestao, 2026-09-23): a varredura das
  // fontes leva de minutos a mais de uma hora, e quem digita uma palavra quer
  // ver JA o que o sistema tem. Consulta o que foi coletado, com a mesma
  // regra das abas (a Regra 1 vale no servidor), enquanto a pessoa digita.
  const [debounce, setDebounce] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounce(busca.trim()), 300);
    return () => clearTimeout(t);
  }, [busca]);
  // `aguardando` (D17, 2026-10-05): quem busca uma oportunidade pelo nome tem
  // de achá-la também enquanto ela espera a leitura do edital. Sem o portão
  // ligado ninguém tem essa faixa, e a busca é a de sempre.
  const filtroAcervo: Filtros = {
    busca: debounce,
    faixa: ["avaliar", "aguardando", "passar"],
    ordem: "score",
  };
  const noAcervo = useQuery({
    queryKey: ["acervo-busca", debounce],
    queryFn: () => api.listar(filtroAcervo, 0, 6),
    enabled: debounce.length >= 2,
  });

  // Só quando UMA fonte está escolhida: "Ambas" não tem formulário próprio, e
  // misturar os campos de duas fontes num painel só seria inventar um contrato
  // que nenhuma das duas tem.
  const catalogo = useQuery({
    queryKey: ["filtros", fonte],
    queryFn: () => api.filtrosDaFonte(fonte),
    enabled: !!fonte,
    // O backend já guarda 15 min; isto evita o ida-e-volta a cada clique no
    // rádio, que é o gesto que mais se repete nesta tela.
    staleTime: 5 * 60 * 1000,
  });

  const opcoes = [
    // "Todas", e não mais "Ambas": com a DadosAPI e o Sebrae são quatro. A
    // paga só entra aqui com `dadosapi.automatica` ligada (`runner.main`).
    { valor: "", nome: "Todas as fontes" },
    ...(fontes.data ?? []).map((f) => ({ valor: f, nome: rotulo(f) })),
  ];

  async function buscar(e: FormEvent) {
    e.preventDefault();
    if (mergulhando) return;
    setErro(null);
    setMergulhando(true);
    try {
      // A rodada é pedida ANTES do mergulho: se a API recusar, o usuário vê o
      // erro na própria tela em vez de mergulhar para o nada.
      const escolhidos = filtros[fonte];
      const p = await api.iniciarRodada(fonte, {
        palavra: busca.trim() || undefined,
        // Objeto vazio não vai: na coluna `filtros` do run, nulo significa
        // "varredura ampla" e `{}` significaria "alguém montou um filtro
        // vazio". São coisas diferentes e o histórico as separa.
        filtros:
          escolhidos && Object.keys(escolhidos).length ? escolhidos : undefined,
      });
      await mergulhar();
      navegar(`/varredura/${p.rodada_id}`);
    } catch (x) {
      setMergulhando(false);
      repousar();
      setErro(x instanceof Error ? x.message : "não deu para iniciar");
    }
  }

  return (
    <div className={css.tela}>
      <div className={`${css.centro} ${mergulhando ? css.dissolvendo : ""}`}>
        {/* A pergunta num <span>: no desktop ela segue na mesma frase, e no
            celular vira a linha de baixo, menor — o título deixa de ser uma
            caixa de quatro linhas curtas. */}
        <h1 className={css.saudacao}>
          {saudacao()}{" "}
          <span className={css.pergunta}>Pronto para analisar as oportunidades do dia?</span>
        </h1>

        {/* O CAMPO NO MEIO DA TELA, O BOTÃO AO LADO (escolha do Pedro,
            2026-10-05). `.barra` envolve os dois: no desktop ela é uma grade
            de três colunas — espaçador, campo, botão —, e o espaçador, da
            largura do botão, é o que põe o campo embaixo do título. No
            celular ela some (`display: contents`) e o campo e a lupa voltam a
            ser os dois itens da linha. */}
        <div className={css.buscaColuna}>
          <form onSubmit={(e) => void buscar(e)} className={css.form}>
            {/* A FONTE DENTRO DA BARRA (20/10, escolha do Pedro entre três
                amostras): a lupa, o campo, a fonte num seletor depois de um
                filete e o botão redondo com a seta — uma peça só. Era uma
                linha de rádios embaixo do campo (e, no celular, um botão que
                a abria). O seletor nativo traz o teclado e o leitor de tela. */}
            <div className={css.barra}>
            <svg className={css.lupaBarra} width="20" height="20" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" />
              <path d="m10.6 10.6 3 3" />
            </svg>
            <input
              value={busca}
              onChange={(e) => setBusca(e.currentTarget.value)}
              placeholder="Uma palavra, ou vazio para o vocabulário inteiro"
              aria-label="Palavra a procurar nas fontes"
              minLength={2}
              disabled={!podeOperar || mergulhando}
              // Só valem no teclado virtual. O corretor trocava sigla de
              // domínio ("ead", "lms") antes de uma varredura que leva até
              // uma hora, e a tecla de envio passa a dizer "Buscar".
              // `spellCheck` fica como está: é o sublinhado do desktop.
              enterKeyHint="search"
              autoCapitalize="none"
              autoCorrect="off"
            />
            <label className={css.fonteNaBarra}>
              <span className={css.fonteEm}>em</span>
              <select
                value={fonte}
                onChange={(e) => setFonte(e.currentTarget.value)}
                disabled={!podeOperar || mergulhando}
                aria-label="Fonte"
              >
                {opcoes.map((o) => (
                  <option key={o.valor || "todas"} value={o.valor}>
                    {o.nome}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={!podeOperar || mergulhando}
              aria-label="Buscar nas fontes"
            >
              <svg className={css.seta} width="20" height="20" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </button>
            </div>
          </form>

          {fonte && catalogo.data && catalogo.data.length > 0 && (
            <FiltrosDaFonte
              filtros={catalogo.data}
              valores={filtros[fonte] ?? {}}
              mudou={(v) => setFiltros((f) => ({ ...f, [fonte]: v }))}
              desabilitado={!podeOperar || mergulhando}
            />
          )}
          {fonte && catalogo.isError && (
            <p className={css.aviso}>
              Não deu para ler os filtros do {rotulo(fonte)}. A busca continua
              funcionando — sem filtro, como antes.
            </p>
          )}

          {/* Diz o que o campo vazio faz. Sem isto, "vazio" parece falta de
              preenchimento em vez da opção normal — que é o caso comum. */}
          {/* O QUE VAI SER PROCURADO, por extenso — e a frase muda quando o
              campo tem texto, porque aí o vocabulário NÃO é usado.

              `runner.py` faz `vocabulario.carrega([palavra])` quando vem uma
              palavra: ela SUBSTITUI a tabela naquela execução, não soma a ela.
              Uma lista de termos sempre visível afirmaria o falso exatamente
              no caso em que a pessoa digitou algo. */}
          {busca.trim() ? (
            <p className={css.nota}>
              Esta busca usa só <b>{busca.trim()}</b> — o vocabulário fica de
              fora desta varredura. Apague o campo para procurar os{" "}
              {ativos.data?.length ?? ""} termos.
            </p>
          ) : (
            /* COLAPSADO POR PADRÃO. Dezoito chips empurram o campo de busca
               para longe do topo, e a pergunta "quais termos?" é ocasional —
               quem chega aqui quer buscar. O `<summary>` mantém a contagem
               sempre visível, que era a informação que já existia, e o resto
               fica a um clique. `<details>` e não estado em React pelo mesmo
               motivo dos menus da navbar: teclado e leitor de tela já vêm
               prontos. */
            <details className={css.nota}>
              <summary>
                Sem texto, procura{" "}
                {ativos.data ? `os ${ativos.data.length} termos` : "os termos"}{" "}
                do vocabulário — ver quais
              </summary>
              {/* Os termos são gravados sem acento e em minúsculas porque o
                  casador normaliza os dois lados antes de comparar
                  (`funil/vocabulario.py`). Mostrar a forma normalizada é
                  mostrar o que de fato vai às fontes. */}
              <ul className={css.termos}>
                {(ativos.data ?? []).map((t) => (
                  <li key={t.termo} title={t.nota ?? undefined}>
                    {t.termo}
                  </li>
                ))}
              </ul>
              <p className={css.notaFina}>
                O vocabulário é o portão da coleta: o que não casa com um destes
                nunca vira linha no banco. Editar em{" "}
                <Link to="/vocabulario">Vocabulário</Link>.
              </p>
            </details>
          )}

          {debounce.length >= 2 && (
            <section className={css.acervo} aria-live="polite">
              <h2>
                Já no acervo
                {noAcervo.data ? ` · ${noAcervo.data.total}` : ""}
              </h2>
              {noAcervo.isPending && <p className={css.nota}>Procurando…</p>}
              {noAcervo.isError && (
                <p className={css.erro}>Não deu para consultar o acervo.</p>
              )}
              {noAcervo.data && noAcervo.data.total === 0 && (
                <p className={css.nota}>
                  Nada com “{debounce}” no que já foi coletado. A varredura
                  abaixo procura nas fontes.
                </p>
              )}
              <ul>
                {(noAcervo.data?.itens ?? []).map((i) => (
                  <li key={i.id}>
                    <Link to={`/licitacao/${i.id}`}>
                      <span className={css.acervoObjeto}>{i.objeto}</span>
                      <span className={css.acervoMeta}>
                        {[i.orgao, i.uf].filter(Boolean).join(" · ")}
                        {i.score != null ? ` · score ${i.score}` : ""}
                        {i.decisao === "descartada" ? " · reprovada" : ""}
                        {i.decisao === "aprovada" ? " · aprovada" : ""}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              {noAcervo.data && noAcervo.data.total > 0 && (
                <Link
                  className={css.acervoTodas}
                  to={`/oportunidades?busca=${encodeURIComponent(debounce)}&faixa=avaliar&faixa=aguardando&faixa=passar`}
                >
                  Ver as {noAcervo.data.total} em Oportunidades
                </Link>
              )}
            </section>
          )}

          {!podeOperar && (
            <p className={css.aviso}>
              Seu acesso é de leitura. Disparar varredura consome as requisições
              da conta do Licit Mais e é restrito a operadores — mas você pode
              ver tudo que já foi coletado em <b>Oportunidades</b>.
            </p>
          )}
          {erro && <p className={css.erro}>{erro}</p>}

          {atual.data && !mergulhando && (
            <button
              type="button"
              className={css.emCurso}
              onClick={() => navegar(`/varredura/${atual.data!.rodada_id}`)}
            >
              <i aria-hidden="true" />
              Uma varredura de “{atual.data.termo}” está em curso ·{" "}
              {Math.round((atual.data.fracao ?? 0) * 100)}% — acompanhar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
